import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Clock, Warning, PaperPlaneTilt, Bell, BellSlash,
  ArrowRight, X as XIcon, ArrowClockwise, Info,
} from "@phosphor-icons/react";
import client from "../lib/api";

const money = (n) =>
  "$" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STAGE_META = {
  15: { label: "15-day", tone: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  30: { label: "30-day", tone: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  60: { label: "60-day", tone: "text-orange-300", bg: "bg-orange-500/10", border: "border-orange-500/30" },
  90: { label: "FINAL", tone: "text-rose-300", bg: "bg-rose-500/10", border: "border-rose-500/40" },
};

/**
 * Payment Reminders — auto-dunning card for the Invoices page.
 *
 * Renders a summary of pending customer reminders (invoices overdue by
 * 15/30/60/90 days) plus a single "Send batch" action. Defensive posture:
 *   - Auto-hides when there are zero pending reminders AND auto-dunning is off
 *   - Auto-send is OPT-IN (safer default because these hit real customer inboxes)
 *   - Preview drawer lets the user see EXACTLY who will be emailed before sending
 */
export default function PaymentRemindersBar() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/payment-reminders/preview");
      setPreview(data);
    } catch {/* silent — hide the bar if preview fails */}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !preview) return null;
  if (!preview) return null;

  const t = preview.total_pending || 0;
  const totalMoney = preview.total_amount_overdue || 0;
  const autoOn = preview.auto_dunning_enabled === true;

  // Auto-hide when there's nothing to do AND auto is off
  if (t === 0 && !autoOn) return null;

  const doToggleAuto = async () => {
    if (!autoOn) {
      // Show a confirmation before enabling — auto emails to customers is
      // a big deal, be extra clear about what's about to happen.
      const ok = window.confirm(
        `Enable Auto-Dunning?\n\n` +
        `From tomorrow 9am CT, FreightPilot will automatically email your ` +
        `customers reminder notices for any invoice past due by 15, 30, 60, or 90 days.\n\n` +
        `You can pause any individual invoice from the reminders drawer. Continue?`
      );
      if (!ok) return;
    }
    setTogglingAuto(true);
    try {
      await client.post("/payment-reminders/settings", {
        auto_dunning_enabled: !autoOn,
      });
      toast.success(autoOn ? "Auto-dunning turned OFF" : "Auto-dunning turned ON");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally { setTogglingAuto(false); }
  };

  const doSendBatch = async () => {
    if (t === 0) return;
    const ok = window.confirm(
      `Send ${t} reminder email${t !== 1 ? "s" : ""} to your customers RIGHT NOW?\n\n` +
      `Total overdue: ${money(totalMoney)}\n` +
      `This will hit real customer inboxes. Review the drawer preview first if you're unsure.`
    );
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await client.post("/payment-reminders/send-batch");
      setResult(data);
      toast.success(`Sent ${data.stats.sent}/${data.stats.attempted} reminder${data.stats.attempted !== 1 ? "s" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <>
      <div
        className="card-surface p-3 mb-4 flex items-center gap-3 flex-wrap"
        data-testid="payment-reminders-bar"
      >
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            t > 0 ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"
          }`}>
            {t > 0 ? <Warning size={16} weight="fill" /> : <Bell size={16} weight="fill" />}
          </div>
          <div>
            <div className="text-xs font-semibold text-white leading-tight">
              Payment reminders
            </div>
            <div className="text-[10px] text-neutral-500 leading-tight">
              {t === 0
                ? "All customers current"
                : `${t} pending · ${money(totalMoney)} overdue`}
            </div>
          </div>
        </div>

        {/* Stage badges */}
        {t > 0 && (
          <div
            className="flex items-center gap-1.5 flex-wrap"
            data-testid="payment-reminders-stage-badges"
          >
            {[15, 30, 60, 90].map((s) => {
              const count = preview.by_stage?.[s] || 0;
              if (count === 0) return null;
              const meta = STAGE_META[s];
              return (
                <span
                  key={s}
                  data-testid={`payment-reminders-stage-${s}`}
                  className={`text-[10px] uppercase tracking-widest font-semibold rounded-full px-2 py-0.5 ${meta.tone} ${meta.bg} ${meta.border} border`}
                >
                  {count} · {meta.label}
                </span>
              );
            })}
          </div>
        )}

        <div className="flex-1" />

        {/* Auto-dunning toggle */}
        <button
          onClick={doToggleAuto}
          disabled={togglingAuto}
          data-testid="payment-reminders-auto-toggle"
          className={`text-xs rounded-full px-3 py-1.5 border inline-flex items-center gap-1.5 transition-colors ${
            autoOn
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
              : "border-white/10 text-neutral-400 hover:border-white/20"
          } disabled:opacity-60`}
        >
          {togglingAuto ? (
            <div className="w-3 h-3 rounded-full border-2 border-white/20 border-t-white animate-spin" />
          ) : autoOn ? (
            <Bell size={12} weight="fill" />
          ) : (
            <BellSlash size={12} weight="regular" />
          )}
          Auto-dunning {autoOn ? "ON" : "OFF"}
        </button>

        {t > 0 && (
          <>
            <button
              onClick={() => setShowDrawer(true)}
              data-testid="payment-reminders-preview-btn"
              className="text-xs rounded-full px-3 py-1.5 border border-white/10 text-neutral-300 hover:border-white/20 inline-flex items-center gap-1.5"
            >
              Preview <ArrowRight size={11} weight="bold" />
            </button>
            <button
              onClick={doSendBatch}
              disabled={sending}
              data-testid="payment-reminders-send-batch-btn"
              className="text-xs rounded-full px-4 py-1.5 bg-[#FF5A1F] hover:bg-[#FF6B33] text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {sending ? (
                <>
                  <div className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={12} weight="bold" />
                  Send batch
                </>
              )}
            </button>
          </>
        )}
      </div>

      {/* Preview drawer */}
      {showDrawer && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
          data-testid="payment-reminders-drawer"
        >
          <div className="min-h-full flex items-start justify-center py-8">
            <div className="w-full max-w-2xl bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-1">
                    <Clock size={12} weight="fill" /> Pending customer reminders
                  </div>
                  <div className="text-lg font-semibold text-white">
                    {t} email{t !== 1 ? "s" : ""} · {money(totalMoney)} overdue
                  </div>
                </div>
                <button
                  onClick={() => setShowDrawer(false)}
                  className="text-neutral-500 hover:text-white"
                  data-testid="payment-reminders-drawer-close"
                >
                  <XIcon size={18} />
                </button>
              </div>

              <div className="p-5 max-h-[60vh] overflow-y-auto">
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-neutral-400 flex items-start gap-2 mb-4">
                  <Info size={12} className="flex-shrink-0 mt-0.5" />
                  <span>
                    Each row will receive a personalized reminder email at the tier shown. Tone escalates
                    from a friendly nudge (15-day) to a firm final notice (90-day). Replies from customers
                    land directly in your inbox.
                  </span>
                </div>

                <div className="space-y-2">
                  {preview.pending.map((p) => {
                    const meta = STAGE_META[p.stage];
                    return (
                      <div
                        key={p.invoice_id}
                        data-testid={`payment-reminders-row-${p.invoice_id}`}
                        className={`rounded-lg border ${meta.border} ${meta.bg} p-3 flex items-center gap-3 flex-wrap`}
                      >
                        <span className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${meta.tone} bg-black/40`}>
                          {meta.label}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-white text-sm font-medium truncate">
                            #{p.invoice_number} · {p.customer}
                          </div>
                          <div className="text-xs text-neutral-400">
                            {p.customer_email} · Due {p.due_date} · <b>{p.days_overdue}d overdue</b>
                          </div>
                        </div>
                        <div className="text-white font-mono text-sm">{money(p.amount)}</div>
                      </div>
                    );
                  })}
                  {preview.pending.length === 0 && (
                    <div className="text-neutral-500 italic text-center py-8">
                      No reminders pending — all customers current!
                    </div>
                  )}
                </div>
              </div>

              <div className="p-5 border-t border-white/10 flex gap-2 justify-end">
                <button
                  onClick={() => setShowDrawer(false)}
                  className="text-xs rounded-full px-4 py-2 border border-white/10 text-neutral-300 hover:border-white/20"
                  data-testid="payment-reminders-drawer-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => { setShowDrawer(false); await doSendBatch(); }}
                  disabled={sending || t === 0}
                  className="text-xs rounded-full px-5 py-2 bg-[#FF5A1F] hover:bg-[#FF6B33] text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="payment-reminders-drawer-send-btn"
                >
                  <PaperPlaneTilt size={12} weight="bold" />
                  Send all {t}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Batch send result modal */}
      {result && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md p-4 overflow-y-auto"
          data-testid="payment-reminders-result-modal"
        >
          <div className="min-h-full flex items-start justify-center py-8">
            <div className="w-full max-w-md bg-[#0F0F0F] border border-white/10 rounded-xl overflow-hidden shadow-2xl">
              <div className="p-5 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">
                  <PaperPlaneTilt size={12} weight="fill" /> Batch complete
                </div>
                <button
                  onClick={() => setResult(null)}
                  className="text-neutral-500 hover:text-white"
                  data-testid="payment-reminders-result-close"
                >
                  <XIcon size={18} />
                </button>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-4 gap-2 text-xs">
                  <div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Sent</div>
                    <div className="text-emerald-300 text-2xl font-semibold">{result.stats.sent}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Queued</div>
                    <div className="text-amber-300 text-2xl font-semibold">{result.stats.queued}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Skipped</div>
                    <div className="text-neutral-400 text-2xl font-semibold">{result.stats.skipped}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-neutral-500 uppercase tracking-widest">Errored</div>
                    <div className={`text-2xl font-semibold ${result.stats.error > 0 ? "text-rose-300" : "text-neutral-600"}`}>
                      {result.stats.error}
                    </div>
                  </div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-[11px] text-neutral-400 flex items-start gap-2">
                  <Info size={12} className="flex-shrink-0 mt-0.5" />
                  <span>
                    "Queued" means we hit the send button but the email transport (Resend) hasn't confirmed
                    delivery yet — check your Resend dashboard for details. "Skipped" usually means the
                    reminder was already sent at this stage or the invoice is paused.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
